export const ORGANIZATION_INVITE_CONFLICT_CODES = {
  MEMBERSHIP_ALREADY_ACTIVE: 'MEMBERSHIP_ALREADY_ACTIVE',
  MEMBERSHIP_SUSPENDED: 'MEMBERSHIP_SUSPENDED',
  ACTIVE_CLIENT_IN_SAME_PRACTICE: 'ACTIVE_CLIENT_IN_SAME_PRACTICE',
  EMAIL_ALREADY_REGISTERED: 'EMAIL_ALREADY_REGISTERED',
  SELF_INVITATION_NOT_ALLOWED: 'SELF_INVITATION_NOT_ALLOWED',
};

export const ORGANIZATION_INVITE_ERROR_CODES = {
  ...ORGANIZATION_INVITE_CONFLICT_CODES,
  INVALID_EMAIL: 'INVALID_EMAIL',
  INVITE_FAILED: 'INVITE_FAILED',
};

const INVITE_ERROR_MESSAGES = {
  [ORGANIZATION_INVITE_ERROR_CODES.INVALID_EMAIL]:
    'Enter a valid email address for this invitation.',
  [ORGANIZATION_INVITE_ERROR_CODES.EMAIL_ALREADY_REGISTERED]:
    'An account with this email already exists. The invitation was not created — ask this person to sign in with their existing account after you send a team invite from Practice Management.',
  [ORGANIZATION_INVITE_ERROR_CODES.INVITE_FAILED]:
    'The invitation could not be sent right now. Try again in a moment or contact support if it keeps happening.',
  [ORGANIZATION_INVITE_ERROR_CODES.SELF_INVITATION_NOT_ALLOWED]:
    'You cannot send a team invitation to your own email address.',
  [ORGANIZATION_INVITE_ERROR_CODES.MEMBERSHIP_ALREADY_ACTIVE]:
    'This person is already an active member of your practice.',
  [ORGANIZATION_INVITE_ERROR_CODES.MEMBERSHIP_SUSPENDED]:
    'This person\'s practice membership is suspended. Review their access before sending another invitation.',
  [ORGANIZATION_INVITE_ERROR_CODES.ACTIVE_CLIENT_IN_SAME_PRACTICE]:
    'This email is tied to an active client chart in this practice. Use a different email for a staff invitation.',
};

const CONFLICT_MESSAGES = INVITE_ERROR_MESSAGES;

const RESCIND_ERROR_MESSAGES = {
  MEMBERSHIP_NOT_FOUND: 'This pending invitation could not be found. Refresh the team list and try again.',
  MEMBERSHIP_ALREADY_ACTIVE: CONFLICT_MESSAGES[ORGANIZATION_INVITE_CONFLICT_CODES.MEMBERSHIP_ALREADY_ACTIVE],
  MEMBERSHIP_SUSPENDED: CONFLICT_MESSAGES[ORGANIZATION_INVITE_CONFLICT_CODES.MEMBERSHIP_SUSPENDED],
};

const RESCIND_UNAVAILABLE_MESSAGE = 'Rescinding invites isn\'t available yet';
const GENERIC_RESCIND_ERROR = 'Could not rescind the invitation. Try again or contact support if the problem continues.';
const GENERIC_INVITE_ERROR = 'Could not send the invitation. Try again or contact support if the problem continues.';
const GENERIC_INVITE_SUCCESS = 'Invitation sent successfully.';
const EXISTING_ACCOUNT_INVITE_SUCCESS =
  'Invitation sent. This person already has a Sena account and should sign in with that account to accept.';

export function normalizeOrganizationInviteResult(data) {
  if (!data || typeof data !== 'object') {
    return {
      userReferenceId: null,
      membershipStatus: null,
      existingAccount: false,
      invitationFirstName: null,
    };
  }

  return {
    userReferenceId: data.userReferenceId ?? data.UserReferenceId ?? null,
    membershipStatus: data.membershipStatus ?? data.MembershipStatus ?? null,
    existingAccount: data.existingAccount === true || data.ExistingAccount === true,
    invitationFirstName: data.invitationFirstName ?? data.InvitationFirstName ?? null,
  };
}

export function getOrganizationInviteSuccessMessage(data) {
  const normalized = normalizeOrganizationInviteResult(data);
  if (normalized.existingAccount) {
    return EXISTING_ACCOUNT_INVITE_SUCCESS;
  }
  return GENERIC_INVITE_SUCCESS;
}

export function getOrganizationInviteErrorMessage(error) {
  const code = error?.code ?? error?.body?.code;
  if (code && INVITE_ERROR_MESSAGES[code]) {
    return INVITE_ERROR_MESSAGES[code];
  }

  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (message && /account with this email already exists/i.test(message)) {
    return INVITE_ERROR_MESSAGES[ORGANIZATION_INVITE_ERROR_CODES.EMAIL_ALREADY_REGISTERED];
  }
  if (message) {
    return message;
  }

  return GENERIC_INVITE_ERROR;
}

export function getOrganizationInviteRescindSuccessMessage(result) {
  if (result?.wasAlreadyRevoked === true || result?.WasAlreadyRevoked === true) {
    return 'This invitation was already rescinded.';
  }
  return 'The invitation was rescinded.';
}

export function getOrganizationInviteRescindErrorMessage(error) {
  const code = error?.code ?? error?.body?.code;
  if (error?.status === 404 && !code) {
    return RESCIND_UNAVAILABLE_MESSAGE;
  }
  if (code && RESCIND_ERROR_MESSAGES[code]) {
    return RESCIND_ERROR_MESSAGES[code];
  }

  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (message) {
    return message;
  }

  return GENERIC_RESCIND_ERROR;
}

export function getOrganizationMemberDisplayName(user) {
  const firstName = user?.firstName || user?.invitationFirstName || user?.InvitationFirstName || '';
  const lastName = user?.lastName || '';
  const combined = [firstName, lastName].filter(Boolean).join(' ').trim();
  return combined || 'Pending invite';
}
